import { AnalysisResult } from '../../../Clients/ImportClients/useAnalyseCSV';
import { ColumnMapping, normalizeHeader } from '../../../Clients/ImportClients/autoMap';

export const APPOINTMENT_FIELDS = [
    'date', 'start', 'end', 'professional', 'client', 'type', 'room', 'price', 'notes', 'status',
] as const;

export type AppointmentField = typeof APPOINTMENT_FIELDS[number];

// notes accepts multiple columns (values are joined with newline during parse)
export const APPOINTMENT_MULTI_COLUMN_FIELDS = new Set<string>(['notes']);

// All aliases are stored pre-normalised (lowercase, diacritics stripped, spaces collapsed)
const FIELD_ALIASES: Record<AppointmentField, string[]> = {
    date:         ['data', 'date', 'fecha', 'datum', 'data_marcacao'],
    start:        ['hora', 'hora_inicio', 'horainicio', 'start', 'time'],
    end:          ['horafim', 'hora_fim', 'hora_saida', 'end', 'hora_termino'],
    professional: ['profissional', 'medico', 'therapist', 'service_provider', 'professional', 'doctor', 'terapeuta'],
    client:       ['utente', 'client_name', 'cliente', 'patient', 'doente', 'name'],
    type:         ['especialidade', 'item_name', 'category', 'tipo', 'service', 'servico', 'treatment'],
    room:         ['sala', 'room', 'gabinete', 'cabinet', 'consultorio', 'box'],
    price:        ['valor', 'price', 'price_final', 'preco', 'valor_consulta'],
    notes:        ['observacoes', 'notes', 'obs', 'notas'],
    status:       ['status', 'estado'],
};

// Matches "dd/MM/yyyy ..." or "yyyy-MM-dd ..." with a time component
const COMBINED_DATETIME_RE = /^\d{1,2}\/\d{1,2}\/\d{4}\s+\d{1,2}:\d{2}|^\d{4}-\d{2}-\d{2}.\d{2}:\d{2}/;

function looksLikeCombinedDatetime(colIndex: number, result: AnalysisResult): boolean {
    const sample = result.sampleRows
        .map(row => row[colIndex] ?? '')
        .filter(v => v.trim())
        .slice(0, 5);
    return sample.length > 0 && sample.some(v => COMBINED_DATETIME_RE.test(v.trim()));
}

function findFieldForHeader(
    normalizedHeader: string,
    alreadyMapped: Set<string>,
): AppointmentField | null {
    for (const [fieldId, aliases] of Object.entries(FIELD_ALIASES) as [AppointmentField, string[]][]) {
        if (!APPOINTMENT_MULTI_COLUMN_FIELDS.has(fieldId) && alreadyMapped.has(fieldId)) continue;
        if (aliases.includes(normalizedHeader)) return fieldId;
    }
    return null;
}

export function autoMapAppointments(result: AnalysisResult): ColumnMapping {
    const fieldMap: Record<string, number[]> = {};
    const assignedCols = new Set<number>();
    const alreadyMapped = new Set<string>();

    const assign = (fieldId: string, colIndex: number) => {
        if (!fieldMap[fieldId]) fieldMap[fieldId] = [];
        fieldMap[fieldId].push(colIndex);
        assignedCols.add(colIndex);
        if (!APPOINTMENT_MULTI_COLUMN_FIELDS.has(fieldId)) alreadyMapped.add(fieldId);
    };

    // Pass 1: alias matching
    // If a column would map to 'start' but its sample values contain a full date+time
    // (e.g. "Hora" column with "15/03/2024 10:30"), route it to 'date' instead so the
    // parser can extract both date and time from a single column.
    result.headers.forEach((header, i) => {
        let fieldId = findFieldForHeader(normalizeHeader(header), alreadyMapped);
        if (fieldId === 'start' && !alreadyMapped.has('date') && looksLikeCombinedDatetime(i, result)) {
            fieldId = 'date';
        }
        if (fieldId) assign(fieldId, i);
    });

    // Pass 2: date type fallback → date (if not already mapped)
    if (!alreadyMapped.has('date')) {
        const firstDateCol = result.columnTypes.findIndex((t, i) => t === 'date' && !assignedCols.has(i));
        if (firstDateCol !== -1) assign('date', firstDateCol);
    }

    // phone and email columns are intentionally left unmapped (not appointment fields)

    const unmappedCols = result.headers.map((_, i) => i).filter(i => !assignedCols.has(i));

    return { fieldMap, unmappedCols };
}
